// =============================================================================
// Solmart live search suggestions.
// =============================================================================
// Wires every `[data-sol-suggest]` dropdown (header search panel + shop-head
// field) to the `wc_ajax_solmart_search_suggest` endpoint: debounced fetch,
// thumbnail + name + price rows, keyboard navigation and a "view all results"
// footer link. Pattern follows the parent theme's search-bar.js typeahead.
// The endpoint URL and both visible strings arrive via data attributes
// (see solmart_search_suggest_attrs() in inc/product-search.php).
// Also wires the header search icon: with JS on it opens the inline panel
// instead of navigating; the plain link stays as the no-JS fallback.
// =============================================================================

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

export default function initSearchSuggest() {
	document.querySelectorAll("[data-sol-suggest]").forEach(initSuggestBox);
	document.querySelectorAll("[data-sol-search-toggle]").forEach(initPanelToggle);
}

// =============================================================================
// Header search panel toggle
// =============================================================================

function initPanelToggle(toggle) {
	const wrap = toggle.closest(".sol-header__search");
	const input = wrap && wrap.querySelector(".sol-search__input");
	if (!wrap || !input) {
		return;
	}

	const setOpen = (open) => {
		wrap.classList.toggle("is-open", open);
		toggle.setAttribute("aria-expanded", open ? "true" : "false");
	};

	toggle.addEventListener("click", (event) => {
		event.preventDefault();
		const open = !wrap.classList.contains("is-open");
		setOpen(open);
		if (open) {
			input.focus();
		}
	});

	document.addEventListener("click", (event) => {
		if (wrap.classList.contains("is-open") && !wrap.contains(event.target)) {
			setOpen(false);
		}
	});
}

// =============================================================================
// Suggest dropdown
// =============================================================================

function initSuggestBox(box) {
	const form = box.closest("form");
	const input = form && form.querySelector('input[name="s"]');
	if (!form || !input) {
		return;
	}

	let timer = null;
	let activeIndex = -1;

	const close = () => {
		box.hidden = true;
		box.innerHTML = "";
		activeIndex = -1;
		input.setAttribute("aria-expanded", "false");
	};

	input.addEventListener("input", () => {
		const query = input.value.trim();
		clearTimeout(timer);
		if (query.length < MIN_CHARS) {
			close();
			return;
		}
		timer = setTimeout(() => {
			const url = new URL(box.dataset.solSuggestUrl, window.location.href);
			url.searchParams.set("q", query);
			fetch(url.toString(), { credentials: "same-origin" })
				.then((res) => (res.ok ? res.json() : null))
				.then((json) => {
					// Discard stale responses.
					if (query !== input.value.trim()) {
						return;
					}
					render(box, input, json?.data?.results || [], query, form);
				})
				.catch(close);
		}, DEBOUNCE_MS);
	});

	input.addEventListener("keydown", (event) => {
		const items = [...box.querySelectorAll(".sol-search__item")];

		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			if (!items.length) {
				return;
			}
			event.preventDefault();
			activeIndex =
				event.key === "ArrowDown"
					? Math.min(activeIndex + 1, items.length - 1)
					: Math.max(activeIndex - 1, -1);
			items.forEach((el, i) => {
				el.classList.toggle("is-active", i === activeIndex);
				if (i === activeIndex) {
					el.scrollIntoView({ block: "nearest" });
				}
			});
		} else if (event.key === "Enter") {
			// With a highlighted row, go straight to that product; otherwise
			// let the native form submit reach the results page.
			if (activeIndex >= 0 && items[activeIndex]) {
				event.preventDefault();
				items[activeIndex].click();
			}
		} else if (event.key === "Escape" && !box.hidden) {
			close();
		}
	});

	document.addEventListener("click", (event) => {
		if (!form.contains(event.target)) {
			close();
		}
	});
}

// =============================================================================
// Render
// =============================================================================

function render(box, input, items, query, form) {
	activeReset(box);
	input.setAttribute("aria-expanded", "true");

	if (!items.length) {
		const empty = box.dataset.solSuggestEmpty || "";
		if (!empty) {
			box.hidden = true;
			return;
		}
		box.innerHTML = `<div class="sol-search__state">${format(empty, escHtml(query))}</div>`;
		box.hidden = false;
		return;
	}

	const all = box.dataset.solSuggestAll || 'View all results for "%s"';
	box.innerHTML =
		items.map((item) => renderItem(item, query)).join("") +
		`<a class="sol-search__all" href="${escAttr(resultsUrl(form, query))}">${format(all, escHtml(query))}</a>`;
	box.hidden = false;
}

function renderItem(item, query) {
	const thumb = item.image
		? `<img src="${escAttr(item.image)}" alt="" width="44" height="44" loading="lazy">`
		: "";
	// The Korean name is sent already resolved by the endpoint; it is never
	// highlighted because the scorer only indexes the English title.
	const kr = item.title_kr ? `<span class="sol-search__name-kr notranslate">${escHtml(item.title_kr)}</span>` : "";
	// With a curated Korean line under it the title must stay English, or the
	// language switcher prints the product name twice in Korean.
	const guard = item.title_kr ? ' notranslate' : "";
	return `
<a class="sol-search__item" role="option" href="${escAttr(item.url)}">
	<span class="sol-search__thumb">${thumb}</span>
	<span class="sol-search__text${guard}">
		<span class="sol-search__name">${highlight(item.title, query)}</span>${kr}
	</span>
	<span class="sol-search__price">${item.price_html || ""}</span>
</a>`;
}

function activeReset(box) {
	box.querySelectorAll(".sol-search__item.is-active").forEach((el) => el.classList.remove("is-active"));
}

function resultsUrl(form, query) {
	return `${form.action}?s=${encodeURIComponent(query)}&post_type=product`;
}

function format(template, safeValue) {
	return template.replace("%s", safeValue);
}

function highlight(text, query) {
	const escaped = escHtml(text);
	if (!query) {
		return escaped;
	}
	return escaped.replace(new RegExp(`(${escRegex(query)})`, "gi"), "<mark>$1</mark>");
}

// =============================================================================
// Escaping helpers
// =============================================================================

function escHtml(str) {
	return String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function escAttr(str) {
	return String(str).replace(/"/g, "&quot;");
}

function escRegex(str) {
	return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
